import React, {useContext, useEffect, useState} from "react";
import {Editor} from "@components/editor";
import {useRuleGet, useRuleUpdate} from "@hooks/rule.ts";
import {useParams} from "react-router-dom";
import {RuleStatus, Sequence} from "@types";
import {injectId, uuidv4} from "@utils";
import {Form} from "./components";
import {LoadingButton} from "@components/ui/button-loading.tsx";
import {ContextAppDispatch} from "@app/context.ts";
import {LoadRule} from "./components/load";
import {useTelegramShowBackButton} from "@hooks/telegram.ts";

export const AutomateRulePage: React.FC = React.memo(() => {
    const {id} = useParams();

    if (!id) {
        return <div>Invalid id</div>;
    }

    const [rule, ruleIsLoading, ruleError, fetchRule] = useRuleGet(id);
    const [_, updating, , updateRule] = useRuleUpdate();
    const [ruleName, setRuleName] = useState<string>();
    const [ruleStatus, setRuleStatus] = useState<RuleStatus>();
    const [sequence, setSequence] = useState<Sequence>();
    const dispatch = useContext(ContextAppDispatch);

    const [showLoading, setShowLoading] = useState(true);

    useTelegramShowBackButton();

    // Ensure loading is shown for at least 1 second
    useEffect(() => {
        const minLoadingDuration = setTimeout(() => {
            setShowLoading(false);
        }, 1000);

        return () => clearTimeout(minLoadingDuration);
    }, []);

    // Inject values when rule is loaded
    useEffect(() => {
        if (rule) {
            let injectedSequence = injectId(rule.sequence, uuidv4) as Sequence;
            setRuleName(rule.name);
            setRuleStatus(rule.status);
            setSequence(injectedSequence);
        }
    }, [rule]);

    const isStillLoading = ruleIsLoading || rule == null || sequence == null || showLoading;

    if (isStillLoading) {
        return <LoadRule/>;
    }

    return (
        <div className="w-full flex flex-col space-y-8">
            <Form
                id={id}
                name={ruleName || ''}
                status={ruleStatus || RuleStatus.INACTIVE}
                type={rule.rule}
                onChange={(name, status) => {
                    setRuleName(name);
                    setRuleStatus(status);
                }}
            />

            <Editor
                type={rule.rule}
                sequence={sequence}
                onChange={(sequence) => {
                    setSequence(sequence);
                }}
            />

            <div className="px-10 w-full sticky bottom-4 flex justify-center">
                <LoadingButton
                    className="w-full"
                    onClick={() => {
                        updateRule(id, {name: ruleName, status: ruleStatus, sequence});
                        dispatch({type: "APP_CONFETTI_SHOW"});
                    }}
                    text="Update"
                    loadingText="Updating..."
                    loading={updating}
                />
            </div>
        </div>
    );
});